// ============================================================================
// Adam Platform — Preflight Modal (BUCKET I.8)
// ============================================================================
// Operator-facing dry-run for fleet upgrades. Owner of three things:
//   1. The upgrade target form (version picker, component scope, tenant
//      scope).
//   2. The preflight result table with structured reason codes.
//   3. The "Start upgrade" gating rules: blocked rows block outright;
//      warn rows require an explicit "I understand" acknowledgement.
//
// Principles:
//   * P4 — never hide a blocked row behind a generic "start anyway" button.
//     Operators must see WHY per-deployment before pulling the trigger.
//   * P11 — the component scope dropdown + per-deployment view keeps the
//     upgrade surface observable on both dimensions (which fleet + which
//     component).
// ============================================================================
import { useState, useMemo, Fragment } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { Modal, Button, Spinner, Badge, Toggle } from "./index";
import { apiGet, apiPost } from "../lib/api";
import { useAuth } from "../hooks/useAuth";
// Bucket C Round 2 — typed deployment row wire shape. Type-only so Vite
// never resolves zod at runtime.
import type { FleetDeploymentRow } from "../../../../src/shared/contracts/fleet-deployments.js";

// ----------------------------------------------------------------------------
// Wire types — shared with CP. Kept local to avoid a cross-package import.
// ----------------------------------------------------------------------------
export type PreflightReasonCode =
  | "VERSION_NOT_FOUND"
  | "VERSION_NOT_STABLE"
  | "COMPONENT_COMPAT_MISMATCH"
  | "PLUGIN_INCOMPATIBLE"
  | "PLUGIN_MALFORMED_RANGE"
  | "DEPLOYMENT_NOT_READY"
  | "DEPLOYMENT_STALE_HEALTH"
  | "UPGRADE_IN_FLIGHT"
  | "CONTRACT_DRIFT"
  // I-NEXT-02 — warn reason surfaced only when "Include platform-self" is on.
  | "PLATFORM_SELF_MANUAL_COMPLETION";

export interface PreflightReason {
  code: PreflightReasonCode;
  message: string;
  detail?: unknown;
}

export interface PreflightDecision {
  deploymentId: string;
  tenantId: string;
  status: "go" | "warn" | "blocked";
  reasons: PreflightReason[];
}

interface VersionOption {
  id: string;
  imageTag: string;
  isStable: boolean;
}

type ComponentScope = "all" | "gateway" | "hank" | "plugins_bundle";

interface PreflightModalProps {
  open: boolean;
  onClose: () => void;
  /** Called after a successful start-upgrade so the page can refetch the active upgrade. */
  onUpgradeStarted: () => void;
}

// ----------------------------------------------------------------------------
// Human copy for PreflightReasonCode — kept in one place so the table and the
// tooltip can stay in sync. The map is also exported for tests.
// ----------------------------------------------------------------------------
export const REASON_CODE_COPY: Record<PreflightReasonCode, string> = {
  VERSION_NOT_FOUND: "Target version is not in the catalog",
  VERSION_NOT_STABLE: "Target version is not marked stable",
  COMPONENT_COMPAT_MISMATCH: "Component compatibility mismatch",
  PLUGIN_INCOMPATIBLE: "One or more plugins are incompatible",
  PLUGIN_MALFORMED_RANGE: "A plugin has a malformed requires range",
  DEPLOYMENT_NOT_READY: "Deployment is not in a ready state",
  DEPLOYMENT_STALE_HEALTH: "Deployment health is stale",
  UPGRADE_IN_FLIGHT: "Another upgrade is already running for this deployment",
  CONTRACT_DRIFT: "Target version expects a newer control-plane contract",
  PLATFORM_SELF_MANUAL_COMPLETION:
    "Platform-self — tag record only; run scripts/deploy-azure.sh on the VM",
};

export function PreflightModal({ open, onClose, onUpgradeStarted }: PreflightModalProps) {
  const [toTag, setToTag] = useState<string>("");
  const [component, setComponent] = useState<ComponentScope>("all");
  const [scopeMode, setScopeMode] = useState<"all" | "specific">("all");
  const [selectedTenantIds, setSelectedTenantIds] = useState<string[]>([]);
  const [ackWarnings, setAckWarnings] = useState<boolean>(false);
  // I-NEXT-02: opt-in to target the platform-self deployment. UI-level guard
  // only shows the checkbox when the operator is platform_admin; server
  // enforces the same gate (the UI check is best-effort presentation).
  const [allowPlatformSelf, setAllowPlatformSelf] = useState<boolean>(false);
  const [preflightDecisions, setPreflightDecisions] = useState<PreflightDecision[] | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const { role } = useAuth();
  const isPlatformAdmin = role === "platform_admin";

  // --- Data queries: versions + deployments pickers.
  const { data: versionsData } = useQuery({
    queryKey: ["versions", "preflight-modal"],
    queryFn: () => apiGet<{ versions: VersionOption[] }>("/api/versions"),
    enabled: open,
  });

  const { data: deploymentsData } = useQuery({
    queryKey: ["fleet-deployments", "preflight-modal"],
    queryFn: () => apiGet<{ deployments: FleetDeploymentRow[] }>("/api/fleet/deployments"),
    enabled: open && scopeMode === "specific",
  });

  const versions = versionsData?.versions ?? [];
  const deployments = deploymentsData?.deployments ?? [];

  // --- Mutations: preflight + start-upgrade.
  const preflightMutation = useMutation({
    mutationFn: async () => {
      const tenantIds =
        scopeMode === "specific" && selectedTenantIds.length > 0 ? selectedTenantIds : undefined;
      return apiPost<{ decisions: PreflightDecision[] }>("/api/fleet/upgrade/preflight", {
        toTag,
        component,
        tenantIds,
        // I-NEXT-02 — only send when opted-in so the default payload stays
        // identical for non-admin callers (server still default-excludes).
        ...(allowPlatformSelf ? { allowPlatformSelf: true } : {}),
      });
    },
    onSuccess: (data) => {
      setPreflightDecisions(data.decisions);
      setErrorMessage(null);
    },
    onError: (err: Error) => {
      setPreflightDecisions(null);
      setErrorMessage(err.message);
    },
  });

  const startUpgradeMutation = useMutation({
    mutationFn: async () => {
      const tenantIds =
        scopeMode === "specific" && selectedTenantIds.length > 0 ? selectedTenantIds : undefined;
      return apiPost<{ ok: boolean }>("/api/fleet/upgrade", {
        toTag,
        component,
        tenantIds,
        // Multi-stage default from BUCKET I.1 — 5% → 25% → 100%.
        canary: { stages: [5, 25, 100] },
        ...(allowPlatformSelf ? { allowPlatformSelf: true } : {}),
      });
    },
    onSuccess: () => {
      onUpgradeStarted();
      resetAndClose();
    },
    onError: (err: Error) => {
      setErrorMessage(err.message);
    },
  });

  const resetAndClose = () => {
    setToTag("");
    setComponent("all");
    setScopeMode("all");
    setSelectedTenantIds([]);
    setAckWarnings(false);
    setAllowPlatformSelf(false);
    setPreflightDecisions(null);
    setErrorMessage(null);
    setExpandedRows(new Set());
    onClose();
  };

  const toggleExpanded = (deploymentId: string) => {
    setExpandedRows((prev) => {
      const next = new Set(prev);
      if (next.has(deploymentId)) next.delete(deploymentId);
      else next.add(deploymentId);
      return next;
    });
  };

  const toggleTenantSelected = (tenantId: string) => {
    setSelectedTenantIds((prev) =>
      prev.includes(tenantId) ? prev.filter((id) => id !== tenantId) : [...prev, tenantId],
    );
  };

  // --- Gating logic for "Start upgrade".
  const { hasBlocked, hasWarn, allGo } = useMemo(() => {
    if (!preflightDecisions || preflightDecisions.length === 0) {
      return { hasBlocked: false, hasWarn: false, allGo: false };
    }
    return {
      hasBlocked: preflightDecisions.some((d) => d.status === "blocked"),
      hasWarn: preflightDecisions.some((d) => d.status === "warn"),
      allGo: preflightDecisions.every((d) => d.status === "go"),
    };
  }, [preflightDecisions]);

  // I-NEXT-02: when platform-self is opted-in, the manual-completion caveat
  // is critical enough that we gate on BOTH acknowledgements being set —
  // the platform-self checkbox alone AND the warn-ack toggle. Prevents a
  // "checkbox drift" scenario where someone clicks the platform-self box,
  // glances at the preflight, and starts without reading the banner.
  const canStart =
    preflightDecisions !== null &&
    preflightDecisions.length > 0 &&
    !hasBlocked &&
    (allGo || (hasWarn && ackWarnings)) &&
    (!allowPlatformSelf || ackWarnings);

  return (
    <Modal open={open} onClose={resetAndClose} title="Upgrade fleet" size="lg">
      <div className="space-y-4">
        {/* Step 1: target + scope form */}
        <div className="space-y-3">
          <div>
            <label
              className="block text-xs font-medium mb-1"
              style={{ color: "var(--c-text-secondary)" }}
            >
              Target version
            </label>
            <select
              data-testid="preflight-version-select"
              value={toTag}
              onChange={(e) => setToTag(e.target.value)}
              className="w-full rounded-lg px-3 py-2 text-sm"
              style={{
                backgroundColor: "var(--c-input-bg)",
                border: "1px solid var(--c-border)",
                color: "var(--c-text)",
              }}
            >
              <option value="">— pick a version —</option>
              {versions.map((v) => (
                <option key={v.id} value={v.imageTag}>
                  {v.imageTag}
                  {v.isStable ? " (stable)" : " (candidate)"}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label
              className="block text-xs font-medium mb-1"
              style={{ color: "var(--c-text-secondary)" }}
            >
              Component scope
            </label>
            <select
              data-testid="preflight-component-select"
              value={component}
              onChange={(e) => setComponent(e.target.value as ComponentScope)}
              className="w-full rounded-lg px-3 py-2 text-sm"
              style={{
                backgroundColor: "var(--c-input-bg)",
                border: "1px solid var(--c-border)",
                color: "var(--c-text)",
              }}
            >
              <option value="all">All components</option>
              <option value="gateway">Gateway only</option>
              <option value="hank">Hank only</option>
              <option value="plugins_bundle">Plugins bundle only</option>
            </select>
          </div>

          <div>
            <label
              className="block text-xs font-medium mb-1"
              style={{ color: "var(--c-text-secondary)" }}
            >
              Tenant scope
            </label>
            <div className="flex items-center gap-4 text-xs" style={{ color: "var(--c-text)" }}>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="scope"
                  checked={scopeMode === "all"}
                  onChange={() => setScopeMode("all")}
                />
                All fleet
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="scope"
                  checked={scopeMode === "specific"}
                  onChange={() => setScopeMode("specific")}
                />
                Specific tenants
              </label>
            </div>
          </div>

          {scopeMode === "specific" && (
            <div
              className="max-h-40 overflow-y-auto rounded-md border p-2"
              style={{ borderColor: "var(--c-border)" }}
              data-testid="preflight-tenant-picker"
            >
              {deployments.length === 0 ? (
                <span className="text-xs" style={{ color: "var(--c-text-faint)" }}>
                  Loading deployments…
                </span>
              ) : (
                deployments.map((d) => (
                  <label
                    key={d.tenantId}
                    className="flex items-center gap-2 py-1 cursor-pointer text-xs"
                    style={{ color: "var(--c-text)" }}
                  >
                    <input
                      type="checkbox"
                      checked={selectedTenantIds.includes(d.tenantId)}
                      onChange={() => toggleTenantSelected(d.tenantId)}
                    />
                    {d.tenantName}
                    <span style={{ color: "var(--c-text-faint)" }}>({d.version})</span>
                  </label>
                ))
              )}
            </div>
          )}

          {/*
            I-NEXT-02 — platform-self opt-in. Only platform_admin operators
            see this control; the server enforces the same gate. Selecting
            it triggers a yellow manual-deploy warning banner that the
            operator must see alongside the warn-ack toggle before Start.
          */}
          {isPlatformAdmin && (
            <div>
              <label
                className="flex items-center gap-2 py-1 cursor-pointer text-xs"
                style={{ color: "var(--c-text)" }}
                data-testid="preflight-allow-platform-self"
              >
                <input
                  type="checkbox"
                  checked={allowPlatformSelf}
                  onChange={(e) => setAllowPlatformSelf(e.target.checked)}
                />
                Include platform-self (orchestrator records intent; manual deploy required)
              </label>
              {allowPlatformSelf && (
                <div
                  className="mt-2 rounded-md px-3 py-2 text-xs"
                  style={{
                    backgroundColor: "color-mix(in srgb, var(--c-warning) 12%, transparent)",
                    color: "var(--c-warning)",
                    border: "1px solid var(--c-warning)",
                  }}
                  data-testid="preflight-platform-self-banner"
                >
                  After apply, run <code>scripts/deploy-azure.sh</code> on the VM to pull
                  the new image and restart the platform gateway. The orchestrator
                  records intent in the deployments table but does not itself perform
                  the image pull.
                </div>
              )}
            </div>
          )}
        </div>

        {/* Step 2: run preflight */}
        <div className="flex items-center gap-3">
          <Button
            data-testid="preflight-run-btn"
            onClick={() => preflightMutation.mutate()}
            disabled={!toTag || preflightMutation.isPending}
            variant="secondary"
          >
            {preflightMutation.isPending ? (
              <span className="inline-flex items-center gap-2">
                <Spinner size={14} />
                Running preflight…
              </span>
            ) : (
              "Run preflight"
            )}
          </Button>
        </div>

        {errorMessage && (
          <div
            className="rounded-md px-3 py-2 text-xs"
            style={{
              backgroundColor: "color-mix(in srgb, var(--c-danger) 12%, transparent)",
              color: "var(--c-danger)",
            }}
          >
            {errorMessage}
          </div>
        )}

        {/* Step 3: result table */}
        {preflightDecisions && (
          <div className="border rounded-md" style={{ borderColor: "var(--c-border)" }}>
            <div
              className="px-3 py-2 text-xs font-medium"
              style={{
                borderBottom: "1px solid var(--c-border)",
                color: "var(--c-text-secondary)",
              }}
            >
              Preflight results ({preflightDecisions.length}{" "}
              {preflightDecisions.length === 1 ? "deployment" : "deployments"})
            </div>
            {preflightDecisions.length === 0 ? (
              <div
                className="px-3 py-4 text-xs"
                style={{ color: "var(--c-text-faint)" }}
                data-testid="preflight-no-deployments"
              >
                No matching deployments.
              </div>
            ) : (
              <table className="w-full text-xs">
                <thead>
                  <tr style={{ borderBottom: "1px solid var(--c-border)" }}>
                    <th
                      className="text-left px-3 py-2"
                      style={{ color: "var(--c-text-secondary)" }}
                    >
                      Tenant
                    </th>
                    <th
                      className="text-left px-3 py-2"
                      style={{ color: "var(--c-text-secondary)" }}
                    >
                      Status
                    </th>
                    <th
                      className="text-left px-3 py-2"
                      style={{ color: "var(--c-text-secondary)" }}
                    >
                      Reasons
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {preflightDecisions.map((d) => (
                    <Fragment key={d.deploymentId}>
                      <tr
                        data-testid={`preflight-row-${d.deploymentId}`}
                        style={{ borderBottom: "1px solid var(--c-border)" }}
                      >
                        <td className="px-3 py-2" style={{ color: "var(--c-text)" }}>
                          {d.tenantId}
                        </td>
                        <td className="px-3 py-2">
                          <Badge
                            color={
                              d.status === "go" ? "green" : d.status === "warn" ? "amber" : "red"
                            }
                          >
                            {d.status}
                          </Badge>
                        </td>
                        <td className="px-3 py-2" style={{ color: "var(--c-text-secondary)" }}>
                          {d.reasons.length === 0 ? (
                            "—"
                          ) : (
                            <button
                              onClick={() => toggleExpanded(d.deploymentId)}
                              style={{
                                background: "none",
                                border: "none",
                                color: "var(--c-primary)",
                                cursor: "pointer",
                                padding: 0,
                                fontSize: "inherit",
                              }}
                              data-testid={`preflight-expand-${d.deploymentId}`}
                            >
                              {d.reasons.length} {d.reasons.length === 1 ? "reason" : "reasons"}{" "}
                              {expandedRows.has(d.deploymentId) ? "(hide)" : "(show)"}
                            </button>
                          )}
                        </td>
                      </tr>
                      {expandedRows.has(d.deploymentId) && (
                        <tr style={{ borderBottom: "1px solid var(--c-border)" }}>
                          <td
                            colSpan={3}
                            className="px-3 py-2"
                            style={{
                              backgroundColor: "var(--c-surface-2)",
                              color: "var(--c-text-secondary)",
                            }}
                          >
                            <ul className="space-y-1">
                              {d.reasons.map((r, idx) => (
                                <li key={idx}>
                                  <strong style={{ color: "var(--c-text)" }}>
                                    {REASON_CODE_COPY[r.code] ?? r.code}
                                  </strong>
                                  : {r.message}
                                </li>
                              ))}
                            </ul>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}

        {/* Step 4: warn-acknowledge + start */}
        {preflightDecisions &&
          preflightDecisions.length > 0 &&
          !hasBlocked &&
          (hasWarn || allowPlatformSelf) && (
            <div className="flex items-center gap-2">
              <Toggle checked={ackWarnings} onChange={setAckWarnings} />
              <span className="text-xs" style={{ color: "var(--c-text-secondary)" }}>
                {allowPlatformSelf
                  ? "I understand the warnings, including the manual platform-self deploy, and want to continue."
                  : "I understand the warnings and want to continue anyway."}
              </span>
            </div>
          )}

        <div className="flex items-center justify-end gap-2 pt-2">
          <Button variant="secondary" onClick={resetAndClose}>
            Cancel
          </Button>
          <Button
            data-testid="preflight-start-btn"
            disabled={!canStart || startUpgradeMutation.isPending}
            onClick={() => startUpgradeMutation.mutate()}
          >
            {startUpgradeMutation.isPending ? (
              <span className="inline-flex items-center gap-2">
                <Spinner size={14} />
                Starting…
              </span>
            ) : (
              "Start upgrade"
            )}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default PreflightModal;
